import { PageHero } from '@components/common';
import { getServiceCard } from '@data/serviceCards';
import {
  HeartPulse,
  RefreshCw,
  Server,
  Radio,
  ShieldCheck,
  Archive,
  Phone,
  CheckCircle,
  ArrowRight,
  Clock,
  Activity,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { CALENDLY_URL } from '@constants';

const offerings = [
  {
    icon: RefreshCw,
    title: 'Business Continuity Planning',
    description:
      'Develop comprehensive continuity plans that identify critical business functions, define recovery priorities, and establish clear procedures to keep operations running during disruption.',
  },
  {
    icon: Server,
    title: 'Disaster Recovery Design',
    description:
      'Design and validate disaster recovery capabilities with defined RTOs and RPOs, tested failover procedures, and documented recovery playbooks tailored to your infrastructure.',
  },
  {
    icon: Archive,
    title: 'Backup Strategy & Validation',
    description:
      'Implement robust backup strategies with regular restoration testing, immutable backups, and off-site redundancy to ensure data is recoverable when you need it most.',
  },
  {
    icon: Radio,
    title: 'Crisis Management & Communications',
    description:
      'Establish crisis communication protocols, stakeholder notification procedures, and executive decision-making frameworks for high-pressure incidents.',
  },
  {
    icon: ShieldCheck,
    title: 'Operational Resilience Frameworks',
    description:
      'Align your resilience programme with regulatory expectations including FCA/PRA Operational Resilience, DORA, and the UK Cyber Security and Resilience Bill.',
  },
  {
    icon: Phone,
    title: 'Supply Chain Resilience',
    description:
      'Assess and mitigate dependencies on critical vendors and third parties, ensuring your business can continue operating even when suppliers are compromised.',
  },
];

const benefits = [
  'Demonstrable ability to continue operating through cyber incidents',
  'Reduced downtime and financial impact from security events',
  'Regulatory compliance with operational resilience requirements',
  'Board and investor confidence in business continuity capabilities',
  'Clear accountability and decision-making during crises',
  'Protection of customer trust and brand reputation',
];

const process = [
  {
    step: '01',
    title: 'Impact Tolerance Discovery',
    description:
      'We identify your critical business services, map dependencies, and define the maximum tolerable disruption your organisation can absorb.',
  },
  {
    step: '02',
    title: 'Gap & Vulnerability Assessment',
    description:
      'We assess your current continuity and recovery capabilities against your impact tolerances, exposing single points of failure and untested assumptions.',
  },
  {
    step: '03',
    title: 'Resilience Programme Design',
    description:
      'We design integrated business continuity, disaster recovery, and crisis management capabilities that match your risk appetite and regulatory obligations.',
  },
  {
    step: '04',
    title: 'Testing & Continuous Improvement',
    description:
      'We establish regular testing regimes - from tabletop exercises to live failover tests - ensuring your resilience capabilities work when reality demands it.',
  },
];

export default function CyberResiliencePage() {
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Cyber Resilience & Business Continuity',
    description:
      'Build the capability to continue operating when cyber incidents strike. Business continuity, disaster recovery, and operational resilience planning.',
    url: 'https://thecisonetwork.com/services/cyber-resilience',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <PageHero
        title="Cyber Resilience & Business Continuity"
        subtitle="Build the capability to continue operating when things go wrong. From business continuity planning to disaster recovery testing, we help you prepare for disruption."
        eyebrow="Our Services"
        backgroundImage={getServiceCard('cyber-resilience')?.image}
        backgroundAlt={getServiceCard('cyber-resilience')?.imageAlt}
        seoTitle="Cyber Resilience & Business Continuity | The CISO Network"
        seoDescription="Expert cyber resilience services including business continuity planning, disaster recovery, operational resilience frameworks, and crisis management."
      />

      {/* Overview */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Resilience Is About Staying in Business
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Incident response tells you what to do when a breach occurs. Cyber resilience
                  ensures your business can keep operating while you respond. It is the difference
                  between a manageable disruption and a terminal crisis.
                </p>
                <p>
                  We help organisations design and validate business continuity, disaster recovery,
                  and crisis management capabilities that match real-world threat scenarios. Our
                  fractional CISOs bring experience from regulated environments where operational
                  resilience is not optional - it is a board-level accountability.
                </p>
                <p>
                  Whether you are preparing for FCA operational resilience requirements, responding
                  to investor due diligence, or simply want confidence that your business can
                  survive a ransomware attack, we provide practical, tested plans that work under
                  pressure.
                </p>
              </div>
            </div>
            <div className="bg-background rounded-2xl p-8 border border-border">
              <div className="flex items-center gap-4 mb-6 justify-center">
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center">
                  <HeartPulse className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground">
                  When You Need This
                </h3>
              </div>
              <div className="flex justify-center">
                <ul className="space-y-4 inline-block text-left">
                  {[
                    'You lack documented business continuity or disaster recovery plans',
                    'Your backups have never been restoration-tested',
                    'You need to satisfy FCA, DORA, or NIS operational resilience requirements',
                    'Investors or board members are asking about continuity capabilities',
                    'A recent incident exposed gaps in your ability to keep operating',
                    'You rely heavily on a small number of critical vendors or systems',
                    'You want to move beyond incident response to genuine operational resilience',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Offerings */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Cyber Resilience Services
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Comprehensive resilience capabilities covering people, process, and technology
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {offerings.map((offering, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-border">
                <div className="h-12 w-12 rounded-xl bg-teal/10 flex items-center justify-center mb-6">
                  <offering.icon className="h-6 w-6 text-teal" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground mb-3">
                  {offering.title}
                </h3>
                <p className="text-muted-foreground">{offering.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid sm:grid-cols-2 gap-6">
                {benefits.map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-teal font-bold text-sm">{i + 1}</span>
                    </div>
                    <p className="text-muted-foreground">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                The Benefits of Cyber Resilience
              </h2>
              <p className="text-muted-foreground mb-6">
                Organisations with mature resilience capabilities recover faster, suffer less
                financial damage, and retain customer trust during incidents. Resilience is not just
                about technology - it is about organisational readiness.
              </p>
              <div className="p-6 bg-background rounded-xl border border-border">
                <h4 className="font-heading font-semibold text-foreground mb-3">
                  Typical Engagement
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration</span>
                    <span className="text-foreground">4-8 weeks</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Deliverable</span>
                    <span className="text-foreground">Resilience programme & documentation</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Output</span>
                    <span className="text-foreground">Tested continuity capabilities</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Our Approach
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              A practical methodology for building resilience that works under real pressure
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-heading font-bold text-teal/10 mb-4">{step.step}</div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {step.title}
                </h3>
                <p className="text-muted-foreground text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-8 text-center">
              Typical Deliverables
            </h2>
            <div className="space-y-4">
              {[
                {
                  title: 'Business Continuity Plan',
                  description:
                    'Documented procedures for maintaining critical operations during disruption, with clear roles and decision trees',
                },
                {
                  title: 'Disaster Recovery Playbook',
                  description:
                    'Step-by-step recovery procedures with defined RTOs, RPOs, and escalation paths for different incident scenarios',
                },
                {
                  title: 'Crisis Communication Framework',
                  description:
                    'Stakeholder notification procedures, template communications, and executive briefing structures',
                },
                {
                  title: 'Backup & Recovery Validation Report',
                  description:
                    'Assessment of current backup coverage, restoration test results, and recommendations for improvement',
                },
                {
                  title: 'Operational Resilience Gap Analysis',
                  description:
                    'Mapped assessment against relevant regulatory frameworks with prioritised remediation roadmap',
                },
              ].map((deliverable, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-background rounded-xl">
                  <div className="h-6 w-6 rounded-full bg-teal flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-foreground">
                      {deliverable.title}
                    </h4>
                    <p className="text-muted-foreground text-sm">{deliverable.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 text-teal text-sm font-medium mb-6">
                <Activity className="h-4 w-4" />
                Complementary Services
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Resilience Works Best Alongside Strong Foundations
              </h2>
              <p className="text-muted-foreground mb-6">
                Cyber resilience does not exist in isolation. It is most effective when combined
                with robust incident response planning, clear security strategy, and regular risk
                assessment. Our fractional CISOs can integrate resilience into a comprehensive
                security programme.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/services/incident-response"
                  className="inline-flex items-center text-teal font-medium hover:underline"
                >
                  Incident Response Planning <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <Link
                  to="/services/security-strategy"
                  className="inline-flex items-center text-teal font-medium hover:underline"
                >
                  Security Strategy <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-border">
              <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center mb-6">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <h3 className="text-xl font-heading font-bold text-foreground mb-4">
                Resilience Health Check
              </h3>
              <p className="text-muted-foreground mb-6">
                Not sure where your resilience gaps are? Our rapid health check assesses your
                current business continuity, disaster recovery, and crisis management capabilities
                against industry standards and regulatory expectations.
              </p>
              <div className="space-y-4">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-medium text-foreground">1-2 weeks</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Deliverable</span>
                  <span className="font-medium text-foreground">Gap analysis & roadmap</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Output</span>
                  <span className="font-medium text-foreground">Prioritised action plan</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Keep Your Business Operating
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Book a discovery call to discuss your cyber resilience needs and how our{' '}
            <Link to="/what-is-a-fractional-ciso" className="text-teal hover:underline">
              fractional CISOs
            </Link>{' '}
            can help you prepare for disruption.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Discovery Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
